import {
    Typography,
    Container,
    useTheme,
    useMediaQuery,
    Grid,
    Box,
    Modal,
    Button,
    Fade,
    Backdrop,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { Tooltip, IconButton, Stack } from "@mui/material";
import { motion } from "framer-motion";
import { useState } from "react";
import CloseIcon from "@mui/icons-material/Close";
import { CustomButton } from "../../utils/CustomTagComponents";
import {
    Accordion,
    AccordionSummary,
    AccordionDetails,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

const UniversitySection = ({ pageData }) => {


    const [open, setOpen] = useState(false);

    const handleOpen = () => setOpen(true);
    const handleClose = () => setOpen(false);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

    const containerVariants = {
        hidden: {},
        visible: {
            transition: {
                staggerChildren: 0.18,
            },
        },
    };

    const fadeUp = {
        hidden: { opacity: 0, y: 40 },
        visible: {
            opacity: 1,
            y: 0,
            transition: { duration: 0.6, ease: "easeOut" },
        },
    };

    const getResponsiveHtml = (html) => {
        if (!html) return "";
        if (!isMobile) return html.replace(/<br\s*\/?>/gi, "").replace(/&nbsp;/gi, " ").replace(/\u00A0/gi, " ").replace(/\[empty_line\]/gi, "<br />");

        return html
            .replace(/font-size:\s*\d+px/gi, "font-size: 18px")
            .replace(/font-weight:\s*900/gi, "font-weight: 700")
            .replace(/<br\s*\/?>/gi, "")
            .replace(/&nbsp;/gi, " ")
            .replace(/\u00A0/gi, " ")
            .replace(/\[empty_line\]/gi, "<br />");
    };

    const getResponsiveTitle = (html) => {
        if (!html) return "";

        let clean = html
            // .replace(/style="[^"]*"/gi, "") // ✅ REMOVE ALL inline styles
            .replace(
                /<(?!ol\b|ul\b)([a-z][a-z0-9]*)\b([^>]*)\sstyle="[^"]*"([^>]*)>/gi,
                "<$1$2$3>"
            )
            .replace(/&nbsp;/gi, "")
            .replace(/<br\s*\/?>/gi, "");

        if (!isMobile) return clean;

        return clean
            .replace(/font-weight:\s*900/gi, "font-weight: 700");
    };

    // Common style for all text
    const textStyle = {
        color: "black",
        mb: 1.5,

        // Better spacing
        lineHeight: 1.7,
        // letterSpacing: "0.2px",

        textAlign: "center",

        // Prevent overflow
        wordBreak: "break-word",
        overflowWrap: "break-word",
        whiteSpace: "normal",

        maxWidth: { xs: "100%", sm: "80%" },
        margin: '0 auto'
    };

    const contentStyles = {
        fontSize: { xs: 12, sm: 13, md: 14 },
        lineHeight: 1.625,
        textAlign: "justify",

        "& p": {
            marginBlockStart: '0px !important',
            marginBottom: "16px",
        },

        "& p:last-of-type": {
            marginBottom: "0px",
        },

        // ✅ REMOVE DEFAULT BULLETS
        "& ul": {
            listStyle: "none",
            paddingLeft: "0px",
            marginBottom: "12px",
        },

        // ✅ CUSTOM LIST ITEM
        "& li": {
            position: "relative",
            paddingLeft: "28px",
            marginBottom: "4px",
            lineHeight: 1.7,
        },
        // ✅ FIRST ITEM
        "& li:first-of-type": {
            marginTop: "-10px",
        },

        // ✅ LAST ITEM
        "& li:last-of-type": {
            marginBottom: "15px",
        },

        // ✅ ADD ARROW ICON
        "& li::before": {
            content: '"\\f35a"',
            fontFamily: "FontAwesome",
            position: "absolute",
            left: '5px',
            top: "2px",
            fontSize: "14px",
            color: "#fd0000",
        },

        "& h1, & h2, & h3, & h4": {
            marginTop: "20px",
            marginBottom: "12px",
            fontWeight: 600,
        },

        "& strong": {
            fontWeight: 600,
        },
    };

    const documents = pageData?.document_list || [];

    const mid = Math.ceil(documents.length / 2); // first 15 items
    const leftColumn = documents.slice(0, mid);
    const rightColumn = documents.slice(mid);
    return (
        <Container
            component={motion.div}
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.2 }}
            maxWidth={false}
            disableGutters
            sx={{
                width: "100%",
                overflow: 'hidden',
                py: { xs: 2, sm: 2, md: 3 },
                px: { xs: 3, sm: 4, md: 6 },
                paddingLeft: { xs: "24px !important", md: "48px !important" },
                paddingRight: { xs: "24px !important", md: "48px !important" }
            }}
        >
            <Grid container sx={{ width: "100%", margin: 0 }}>
                {pageData.title && (
                    <Grid component={motion.div} mb={2} variants={fadeUp} size={{ xs: 12 }}>
                        <Box
                            sx={{
                                display: "inline-block",
                                position: "relative",
                                textAlign: "center",
                                width: "100%",

                                "&::after": {
                                    content: '""',
                                    position: "absolute",
                                    left: "50%",
                                    transform: "translateX(-50%)",
                                    bottom: -8,
                                    width: "200px",
                                    height: "2px",
                                    backgroundColor: "#fd0000",
                                    opacity: "0.5"
                                },
                            }}
                        >
                            <Typography
                                sx={{
                                    fontSize: { xs: "22px", sm: "26px", md: "30px" },
                                    wordBreak: "break-word",
                                    textAlign: "center",
                                    fontWeight: 700,
                                }}
                                dangerouslySetInnerHTML={{
                                    __html: getResponsiveTitle(pageData.title),
                                }}
                            />
                        </Box>
                    </Grid>
                )}

                {pageData.Desc && (
                    <Grid component={motion.div} variants={fadeUp} size={{ xs: 12 }}>
                        <Typography
                            sx={{
                                fontSize: { xs: "14px", sm: "16px" },
                                ...textStyle
                            }}
                            maxWidth={'md'}

                            dangerouslySetInnerHTML={{
                                __html: getResponsiveHtml(pageData.Desc),
                            }}
                        />
                    </Grid>
                )}

                <Grid
                    sx={{
                        mt: 2,
                        display: "grid",
                        gridTemplateColumns: {
                            xs: "1fr",     // mobile → 1 column
                            md: "1fr 1fr", // desktop → 2 columns
                        },
                        columnGap: 2, // horizontal space (right side)
                        alignItems: "start",
                        rowGap: 1,    // vertical space (bottom)
                    }}
                >
                    <Box>
                        {leftColumn.map((item, index) => (
                            <Accordion
                                key={index}
                                disableGutters
                                sx={{
                                    borderRadius: "10px !important",
                                    mb: 1.5,
                                    boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
                                    border: "1px solid #e5e7eb",
                                    "&:before": { display: "none" },
                                    transition: "0.2s",

                                    "&:hover": {
                                        boxShadow: "0 6px 16px rgba(0,0,0,0.08)",
                                    },
                                }}
                            >
                                <AccordionSummary
                                    expandIcon={<ExpandMoreIcon />}
                                    sx={{
                                        minHeight: "50px !important",
                                        px: 2,

                                        "& .MuiAccordionSummary-content": {
                                            margin: "8px 0",
                                        },
                                    }}
                                >
                                    <Typography
                                        sx={{
                                            fontSize: { xs: "14px", sm: "16px", md: "16px" },
                                            fontWeight: 500,
                                            color: "black",
                                        }}
                                    >
                                        {index + 1}. {item.key}
                                    </Typography>
                                </AccordionSummary>

                                {item.value && (
                                    <AccordionDetails
                                        sx={{
                                            background: "#f9fafb",
                                            borderTop: "1px solid #eee",
                                            px: 2,
                                            py: 1.5,
                                        }}
                                    >
                                        <Typography
                                            sx={{
                                                color: "#374151",
                                                lineHeight: 1.7,
                                                ...contentStyles,
                                            }}
                                            dangerouslySetInnerHTML={{ __html: item.value }}
                                        />
                                    </AccordionDetails>
                                )}
                            </Accordion>
                        ))}
                    </Box>

                    <Box>
                        {rightColumn.map((item, index) => (
                            <Accordion
                                key={mid + index}
                                disableGutters
                                sx={{
                                    borderRadius: "10px !important",
                                    mb: 1.5,
                                    boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
                                    border: "1px solid #e5e7eb",
                                    "&:before": { display: "none" },
                                    transition: "0.2s",

                                    "&:hover": {
                                        boxShadow: "0 6px 16px rgba(0,0,0,0.08)",
                                    },
                                }}
                            >
                                <AccordionSummary
                                    expandIcon={<ExpandMoreIcon />}
                                    sx={{
                                        minHeight: "50px !important",
                                        px: 2,

                                        "& .MuiAccordionSummary-content": {
                                            margin: "8px 0",
                                        },
                                    }}
                                >
                                    <Typography
                                        sx={{
                                            fontSize: { xs: "14px", sm: "16px", md: "16px" },
                                            fontWeight: 500,
                                            color: "black",
                                        }}
                                    >
                                        {index + mid + 1}. {item.key}
                                    </Typography>
                                </AccordionSummary>

                                {item.value && (
                                    <AccordionDetails
                                        sx={{
                                            background: "#f9fafb",
                                            borderTop: "1px solid #eee",
                                            px: 2,
                                            py: 1.5,
                                        }}
                                    >
                                        <Typography
                                            sx={{
                                                color: "#374151",
                                                lineHeight: 1.7,
                                                ...contentStyles,
                                            }}
                                            dangerouslySetInnerHTML={{ __html: item.value }}
                                        />
                                    </AccordionDetails>
                                )}
                            </Accordion>
                        ))}
                    </Box>
                </Grid>

            </Grid>
        </Container>
    );
};

export default UniversitySection;